import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanMatchFn, Route } from '@angular/router';

import { filter, map } from 'rxjs';

import { AuthService } from '@pp/core/auth-store';
import { Role } from '@pp/core/entities';
import { PermissionsStore } from '@pp/core/store';
import { ShellComponent } from '@pp/shell';

const activeUser: CanMatchFn = () => {
  const auth = inject(AuthService);

  return auth.activeStatusCheck();
};

const inactiveUser: CanMatchFn = () => {
  const auth = inject(AuthService);

  return auth.inactiveStatusCheck();
};

const walletPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.permissions).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePage(permissionsService.WALLETS_PAGE_PERMISSION_COLLECTION())),
  );
};

const activityPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.permissions).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePage(permissionsService.ACTIVITY_PAGE_PERMISSION_COLLECTION())),
  );
};

const organizationsPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.role).pipe(
    filter(Boolean),
    map(() => permissionsService.isOrganizationsVisible()),
  );
};

const promoPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.role).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePageByRoles([Role.SuperAdmin])),
  );
};

const payOrdersPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.role).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePageByRoles([Role.Admin])),
  );
};

const accountsPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.permissions).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePage(permissionsService.USERS_PAGE_PERMISSION_COLLECTION())),
  );
};

const cardsPermission: CanMatchFn = () => {
  const permissionsService = inject(PermissionsStore);

  return toObservable(permissionsService.permissions).pipe(
    filter(Boolean),
    map(() => permissionsService.canSeePage(permissionsService.CARDS_PAGE_PERMISSION_COLLECTION())),
  );
};

export const appRoutes: Route[] = [
  {
    path: 'signin',
    title: 'Sign In',
    canMatch: [inactiveUser],
    loadChildren: () => import('./auth/signin/signin.routes').then(m => m.signinRoutes),
  },
  {
    path: 'signup',
    title: 'Sign Up',
    canMatch: [inactiveUser],
    loadChildren: () => import('./auth/signup/signup.routes').then(m => m.signupRoutes),
  },
  {
    path: '404',
    title: '404',
    loadComponent: () => import('./handle-pages/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
  {
    path: 'no-team',
    title: 'No team',
    loadComponent: () => import('./handle-pages/no-team/no-team.component').then(m => m.NoTeamComponent),
  },
  {
    path: '',
    component: ShellComponent,
    canMatch: [activeUser],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./handle-pages/default-page-by-role/default-page-by-role.component').then(m => m.DefaultPageByRoleComponent),
        pathMatch: 'full',
      },
      // {
      //   path: 'dashboard',
      //   title: 'Dashboard',
      //   loadChildren: () => import('./features/dashboard/dashboard.routes').then(m => m.dashboardRoutes),
      // },
      {
        path: 'wallets',
        title: 'Wallets',
        canMatch: [walletPermission],
        loadChildren: () => import('./features/wallets-page/wallets-page.routes').then(m => m.walletsPageRoutes),
      },
      {
        path: 'activity',
        title: 'Activity',
        canMatch: [activityPermission],
        loadChildren: () => import('./features/activity/activity.routes').then(m => m.activityRoutes),
      },
      {
        path: 'teams',
        title: 'Teams',
        canMatch: [organizationsPermission],
        loadChildren: () => import('./features/teams/teams.routes').then(m => m.teamsRoutes),
      },
      {
        path: 'promo',
        title: 'Promo',
        canMatch: [promoPermission],
        loadChildren: () => import('./features/promo/promo.routes').then(m => m.promoRoutes),
      },
      {
        path: 'pay-orders',
        title: 'Pay.Orders',
        canMatch: [payOrdersPermission],
        loadChildren: () => import('./features/pay-orders/pay-orders.routes').then(m => m.payOrdersRoutes),
      },
      {
        path: 'users',
        title: 'Users',
        canMatch: [accountsPermission],
        loadChildren: () => import('./features/users/users.routes').then(m => m.usersRoutes),
      },
      {
        path: 'support',
        title: 'Support',
        loadChildren: () => import('./features/support/support.routes').then(m => m.supportRoutes),
      },
      {
        path: 'cards',
        title: 'Cards',
        canMatch: [cardsPermission],
        loadChildren: () => import('./features/cards-page/cards-page.routes').then(m => m.cardsPageRoutes),
      },
      {
        path: '403',
        title: '403',
        loadComponent: () => import('./handle-pages/forbidden/forbidden.component').then(m => m.ForbiddenComponent),
      },
      {
        path: '500',
        title: '500',
        loadComponent: () =>
          import('./handle-pages/internal-server-error/internal-server-error.component').then(m => m.InternalServerErrorComponent),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
